// V5.10 : Étape Documents restructurée avec slots obligatoires
// CNI, RIB, Justificatif de domicile, Dernière facture

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, CheckCircle, AlertCircle, FileText, CreditCard, 
  Home, Receipt, X, Loader2, Eye, ArrowRight, ArrowLeft,
  User, Building2
} from 'lucide-react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { toast } from 'sonner';

// Configuration des documents requis
const REQUIRED_DOCUMENTS = {
  cni: {
    id: 'cni',
    name: 'Pièce d\'Identité',
    description: 'CNI ou Passeport (recto/verso)',
    icon: User,
    required: true,
    acceptedFormats: '.pdf,.jpg,.jpeg,.png',
    maxSize: 5, // MB
    color: 'cyan'
  },
  rib: {
    id: 'rib',
    name: 'RIB',
    description: 'Relevé d\'Identité Bancaire',
    icon: CreditCard,
    required: true,
    acceptedFormats: '.pdf,.jpg,.jpeg,.png',
    maxSize: 2,
    color: 'purple'
  },
  justificatif_domicile: {
    id: 'justificatif_domicile',
    name: 'Justificatif de domicile',
    description: 'Nouveau bail ou titre de propriété',
    icon: Home,
    required: true,
    acceptedFormats: '.pdf,.jpg,.jpeg,.png',
    maxSize: 5,
    color: 'green'
  },
  derniere_facture: {
    id: 'derniere_facture',
    name: 'Dernière facture',
    description: 'Pour récupérer votre PDL ou RIO',
    icon: Receipt,
    required: false,
    acceptedFormats: '.pdf,.jpg,.jpeg,.png',
    maxSize: 5,
    color: 'orange'
  }
};

const DocumentSlot = ({ 
  docConfig, 
  uploadedFile, 
  onUpload, 
  onRemove, 
  uploading,
  missionId 
}) => {
  const inputRef = useRef(null);
  const Icon = docConfig.icon;
  const isUploaded = !!uploadedFile;
  
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Vérifier la taille
    if (file.size > docConfig.maxSize * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${docConfig.maxSize}MB)`);
      return;
    }
    
    await onUpload(docConfig.id, file);
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative p-4 rounded-lg border-2 transition-all ${
        isUploaded 
          ? 'border-success/50 bg-success/5' 
          : docConfig.required 
            ? 'border-orange-400/50 bg-orange-400/5 hover:border-orange-400' 
            : 'border-gray-700 hover:border-gray-600'
      }`}
      data-testid={`doc-slot-${docConfig.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-${docConfig.color}-400/20 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${docConfig.color}-400`} />
          </div>
          <div>
            <p className="text-white font-medium flex items-center gap-2">
              {docConfig.name}
              {docConfig.required && (
                <span className="text-orange-400 text-xs font-mono">*OBLIGATOIRE</span>
              )}
            </p>
            <p className="text-gray-500 text-xs">{docConfig.description}</p>
          </div>
        </div>
        
        {/* Status Badge */}
        {isUploaded ? (
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-success/20">
            <CheckCircle className="w-4 h-4 text-success" />
            <span className="text-success text-xs">Uploadé</span>
          </div>
        ) : docConfig.required ? (
          <div className="flex items-center gap-1 px-2 py-1 rounded bg-orange-400/20">
            <AlertCircle className="w-4 h-4 text-orange-400" />
            <span className="text-orange-400 text-xs">Requis</span>
          </div>
        ) : null}
      </div>
      
      {/* Upload Zone */}
      {isUploaded ? (
        <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-success" />
            <span className="text-white text-sm truncate max-w-[200px]">
              {uploadedFile.filename || uploadedFile.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {uploadedFile.url && (
              <a 
                href={uploadedFile.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-1.5 rounded hover:bg-gray-700"
              >
                <Eye className="w-4 h-4 text-gray-400" />
              </a>
            )}
            <button 
              onClick={() => onRemove(docConfig.id)}
              className="p-1.5 rounded hover:bg-red-400/20"
            >
              <X className="w-4 h-4 text-red-400" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className="p-6 border-2 border-dashed border-gray-700 rounded-lg hover:border-cyan-400/50 cursor-pointer transition-all text-center"
        >
          <input
            ref={inputRef}
            type="file"
            accept={docConfig.acceptedFormats}
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          
          {uploading === docConfig.id ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              <span className="text-cyan-400 text-sm">Upload en cours...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-gray-500" />
              <span className="text-gray-400 text-sm">Cliquez ou glissez-déposez</span>
              <span className="text-gray-600 text-xs">
                {docConfig.acceptedFormats.replace(/\./g, '').toUpperCase()} • Max {docConfig.maxSize}MB
              </span>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

const DocumentsStep = ({ 
  missionId, 
  uploadedDocs = {}, 
  onDocsChange,
  onNext,
  onBack
}) => {
  const [uploading, setUploading] = useState(null);
  const [docs, setDocs] = useState(uploadedDocs);
  
  // Upload d'un document
  const handleUpload = async (docType, file) => {
    setUploading(docType);
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    formData.append('mission_id', missionId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      const newDocs = {
        ...docs,
        [docType]: {
          filename: file.name,
          doc_id: response.data.doc_id,
          uploaded_at: new Date().toISOString()
        }
      };
      
      setDocs(newDocs);
      onDocsChange?.(newDocs);
      
      toast.success(`${REQUIRED_DOCUMENTS[docType].name} uploadé avec succès !`);
      
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(null);
    }
  };
  
  // Supprimer un document
  const handleRemove = async (docType) => {
    try {
      const token = localStorage.getItem('token');
      const docInfo = docs[docType];
      
      if (docInfo?.doc_id) {
        await axios.delete(`${API}/documents/${docInfo.doc_id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
      }
      
      const newDocs = { ...docs };
      delete newDocs[docType];
      setDocs(newDocs);
      onDocsChange?.(newDocs);
      
      toast.success('Document supprimé');
      
    } catch (error) {
      console.error('Erreur suppression:', error);
      // Supprimer localement quand même
      const newDocs = { ...docs };
      delete newDocs[docType];
      setDocs(newDocs);
      onDocsChange?.(newDocs);
    }
  };
  
  // Vérifier si tous les documents obligatoires sont uploadés
  const requiredDocsUploaded = Object.entries(REQUIRED_DOCUMENTS)
    .filter(([_, config]) => config.required)
    .every(([docId, _]) => docs[docId]);
  
  const uploadedCount = Object.keys(docs).length;
  const requiredCount = Object.values(REQUIRED_DOCUMENTS).filter(d => d.required).length;
  
  return (
    <div className="space-y-6">
      {/* En-tête */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
          requiredDocsUploaded ? 'bg-success/20 text-success' : 'bg-orange-400/20 text-orange-400'
        } mb-4`}>
          <FileText className="w-5 h-5" />
          <span className="font-mono text-sm">
            {requiredDocsUploaded 
              ? 'DOCUMENTS COMPLETS' 
              : `${uploadedCount}/${requiredCount} DOCUMENTS REQUIS`
            }
          </span>
        </div>
        <h2 className="font-orbitron text-2xl font-bold text-white mb-2">
          Documents justificatifs
        </h2>
        <p className="text-gray-400 max-w-lg mx-auto">
          Pour traiter votre dossier, nous avons besoin des documents suivants.
          Les documents marqués <span className="text-orange-400">*OBLIGATOIRE</span> sont indispensables.
        </p>
      </motion.div>
      
      {/* Grille des slots de documents */}
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(REQUIRED_DOCUMENTS).map(([docId, config], index) => (
          <DocumentSlot
            key={docId}
            docConfig={config}
            uploadedFile={docs[docId]}
            onUpload={handleUpload}
            onRemove={handleRemove}
            uploading={uploading}
            missionId={missionId}
          />
        ))}
      </div>
      
      {/* Message d'avertissement si documents manquants */}
      {!requiredDocsUploaded && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 bg-orange-400/10 border border-orange-400/30 rounded-lg"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5" />
            <div>
              <p className="text-orange-400 font-medium">Documents manquants</p>
              <p className="text-gray-400 text-sm">
                Vous devez uploader tous les documents obligatoires pour accéder à la signature du mandat.
              </p>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Boutons de navigation */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-gray-600 text-gray-400"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        
        <Button
          onClick={() => onNext(docs)}
          disabled={!requiredDocsUploaded}
          className={`${requiredDocsUploaded 
            ? 'bg-cyan-400 text-black hover:bg-cyan-300' 
            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          } font-orbitron`}
          data-testid="documents-continue-btn"
        >
          {requiredDocsUploaded ? 'Continuer vers le Mandat' : 'Documents manquants'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default DocumentsStep;
